import { useRouter } from 'vue-router'
import { useFetch } from './fetch'
import { useMatterId } from './matterId'
import { objectOmit, resolveOption } from '~/utils'

import type { MatterInfo, MatterInfoKeys, PredictionParameters } from '~/types'
import type { MatterParameters as GetMatterInfoReturn } from '~/types/api'

export const useMatterInfo = createGlobalState(() => {
  const { replace } = useRouter()

  const { matterId } = useMatterId()

  const paramsUrl = computed(() => matterId.value ? `/prediction-parameters/${matterId.value}` : '')

  const { data, isFetching, execute, statusCode, onFetchFinally } = useFetch(paramsUrl).json<GetMatterInfoReturn | null>()

  const matterInfo = computed<MatterInfo | undefined>(() => data.value
    ? {
        clientName: data.value.clientName,
        matterName: data.value.matterDescription,
        principalInjury: resolveOption('derivedInjury', data.value.derivedInjury),
        weightmansReference: data.value.weightmansReference,
        insurersHandler: data.value.insurerHandler,
        caseHandler: data.value.caseHandler,
        accidentDate: data.value.dateOfAccident,
        instructionDate: data.value.dateInstructionsReceived,
      }
    : undefined)

  const matterInfoKeys = ['caseHandler', 'clientName', 'clientReference', 'dateInstructionsReceived', 'dateOfAccident', 'insurerHandler', 'matterDescription', 'weightmansReference'] satisfies MatterInfoKeys

  const initialPredictionParameters = computed<PredictionParameters | undefined>(() => data.value ? objectOmit(data.value, matterInfoKeys) : undefined)

  const isPermitted = computed(() => statusCode.value !== 403)

  const exists = computed(() => statusCode.value !== 204)

  onFetchFinally(() => {
    if (statusCode.value === 403)
      replace('/forbidden')
    else if (statusCode.value === 204)
      replace('/no-content')
  })

  return {
    isFetching,
    isPermitted,
    execute,
    statusCode,
    exists,
    matterInfo,
    initialPredictionParameters,
  }
})
